import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useObservable } from '@hooks/useObservable';
import { useTranslate } from '@hooks/useTranslate';
import { authStore, useAuth } from '@stores/authStore';
import {
  ApartmentTranslations,
  apartmentUtils,
} from '@views/ApartmentPage/ApartmentPage.utils';

import { fetchApartmentsQuery } from './apartment.queries';
import { apartmentStore } from './apartmentStore';

export const useApartment = (apartmentId?: string) => {
  const { t } = useTranslate();
  const { isAuthenticated } = useAuth();
  const selectedApartmentId = useObservable(
    () => apartmentId ?? apartmentStore.selectedApartmentID,
    [apartmentId]
  );

  const { data, ...restQueryProps } = useQuery({
    ...fetchApartmentsQuery,
    /* check authStore.isAuthenticated state on re-renders but don't trigger
     * useQuery on authStore.isAuthenticated change (since it would require wrapping
     * the hook inside an observer somehow). This is because useQuery hooks are only
     * invoked when the user is accessing an authenticated route, i.e. has authenticated
     * already. However, when logging-out, authStore.isAuthenticated state changes before
     * unmounting the authenticated routes and, therefore, up-to-date authentications
     * state must be checked to avoid illegal API calls on log-out */
    enabled: isAuthenticated && authStore.isAuthenticated,
  });

  const apartment = useMemo(
    () => data?.find(({ apartmentId }) => apartmentId === selectedApartmentId),
    [selectedApartmentId, data]
  );

  const translate = useCallback(
    <AttributeT extends keyof ApartmentTranslations>(
      attribute: AttributeT,
      value: keyof ApartmentTranslations[AttributeT] | undefined | null
    ) => apartmentUtils.translate(attribute, value, t),
    [t]
  );

  return { apartment, translate, ...restQueryProps };
};
